/**
 * The other end of a password-reset email.
 *
 * The person here has already been locked out once today. Everything below is
 * shaped by that:
 *
 *  - **the link is checked before anything is asked.** A dead link should say
 *    so immediately, not after somebody has chosen and typed a new password
 *    twice.
 *  - **the address is shown, never asked for.** It was fixed when the reset
 *    was issued. Letting the holder of a link type an address would turn a
 *    forwarded email into an account takeover.
 *  - **there is a way onward from every dead end.** An expired link offers to
 *    send a fresh one rather than leaving somebody on a page that only says no.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { Button, Input, Spinner } from '../../components/ui';

interface ResetCheck {
  valid: boolean;
  email: string | null;
}

export function ResetPasswordPage(): ReactNode {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Checked with GET, which deliberately does not consume the token.
   *
   * The reset is spent by the POST below and nothing else, so opening the
   * email on a phone and finishing on a laptop still works.
   */
  const check = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => api.get<ResetCheck>(`/auth/reset-password/${token}`),
    retry: false,
  });

  if (check.isLoading) {
    return (
      <AuthLayout title="Checking your link">
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (check.isError || !check.data?.valid) {
    return (
      <AuthLayout title="This link cannot be used">
        <p className="text-sm text-slate-600">
          Password reset links work once and expire quickly, so an old email or one that has
          already been used will not open.
        </p>
        <Button className="mt-6 w-full" onClick={() => navigate('/forgot-password')}>
          Send me a new link
        </Button>
        <Button className="mt-2 w-full" variant="secondary" onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      </AuthLayout>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    // Checked here rather than on the server: the server has no business
    // knowing what somebody typed into a second box.
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      // Straight to sign-in rather than signing them in automatically: proving
      // the new password works now is worth more than saving one step.
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not set your new password.',
      );
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="You will use this to sign in from now on"
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            Sign in instead
          </Link>
        </>
      }
    >
      {check.data.email && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Resetting the password for{' '}
          <strong className="text-slate-900">{check.data.email}</strong>
        </p>
      )}

      <form className="mt-5 space-y-4" onSubmit={submit} noValidate>
        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <Input
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <Input
          label="Confirm new password"
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Set new password
        </Button>
      </form>
    </AuthLayout>
  );
}
